import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import useClips from "../../hooks/useClips";
import useLanguage from "../../hooks/useLanguage";

import CommonPage from "../../components/common-page";
import ClipEditor from "./components/ClipEditor";
import { LTR, dayLabel, fmtClock, groupByDay, showWindow } from "./utils";

function ShowCard({ show, text, onPick }) {
  return (
    <Card variant="outlined">
      <CardActionArea onClick={() => onPick(show)} sx={{ p: 2, height: "100%" }}>
        <Stack spacing={0.75} sx={{ height: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {show.title || text.untitled}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", ...LTR }}>
            {showWindow(show)}
          </Typography>
          <Box sx={{ pt: 0.5 }}>
            <Chip
              size="small"
              label={fmtClock(show.endOffsetSec - show.startOffsetSec)}
              sx={LTR}
            />
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export default function ClipToArticle() {
  const [
    {
      pages: { clipToArticle: text },
    },
  ] = useLanguage();
  const { listShows } = useClips();

  const [shows, setShows] = useState([]);
  const [dayIdx, setDayIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  const fetchShows = async () => {
    setLoading(true);
    try {
      const response = await listShows();
      setShows(response?.items || []);
    } catch (_) {
      setShows([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    fetchShows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const days = useMemo(() => groupByDay(shows), [shows]);
  const activeDay = days[Math.min(dayIdx, Math.max(days.length - 1, 0))];

  if (initialLoad && loading)
    return (
      <CommonPage title={text.title} subtitle={text.subtitle}>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2, maxWidth: 380 }} />
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={96} sx={{ mb: 1.5 }} />
        ))}
      </CommonPage>
    );

  return (
    <CommonPage
      title={text.title}
      subtitle={text.subtitle}
      action={
        selected
          ? undefined
          : {
              label: text.refresh,
              icon: <RefreshRoundedIcon fontSize="small" />,
              onClick: fetchShows,
              disabled: loading,
            }
      }
    >
      {selected ? (
        <ClipEditor
          show={selected}
          text={text}
          onBack={() => setSelected(null)}
          onGenerated={() => setSelected(null)}
        />
      ) : !days.length ? (
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
          <Typography variant="h6">{text.empty.title}</Typography>
          <Typography variant="body2">{text.empty.subtitle}</Typography>
        </Box>
      ) : (
        <>
          <Tabs
            value={Math.min(dayIdx, days.length - 1)}
            onChange={(_e, v) => setDayIdx(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            {days.map((day) => (
              <Tab key={day.dayKey} label={dayLabel(day.dayKey, text)} />
            ))}
          </Tabs>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 2,
            }}
          >
            {activeDay?.shows.map((show) => (
              <ShowCard
                key={`${show.recordingKey}-${show.startOffsetSec}`}
                show={show}
                text={text}
                onPick={setSelected}
              />
            ))}
          </Box>
        </>
      )}
    </CommonPage>
  );
}
